import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import Canvas from "../canvas";
import { useBoardFile } from "../../context/boardFileContext";
import { useCanvas } from "../../context/canvasContext";
import { v1 as uuid } from "uuid";
import { debounce } from 'lodash';

import {
  addObj,
  addPath,
  addText,
  emitCanvasFile,
  emitDrawingPath,
  emitModify,
  modifyObj,
  removeObject,
  addImage,
  emitClonedObj,
  addClonedObject,
  addToGroup,
  emitRoomID,
  emitCanvasData,
  onBackgroundColorChange,
  clearCanvasListen,
  onShapeColorChange,
  ListenUndo,
  ListenRedo,
  reloadStack,
  listenSendToBack,
  listenBringToFront,
  onStrokeColorChange,
  listenUngroup,
  listenUpdateAll
} from "../../socket";
import axios from "axios";
import { boardServices, workwiseServices, templateService } from "../../api";
import { useDocument } from "../../context/documentInfoContex";
import { centerAllObjectsInViewport, formatFileSize, isOffline, removeOfflineData, resolveDataConflict, saveToLocalStorage, processTemplateData, removeTemplateIdFromURL } from "../../utils/helper";
import WaterMark from "../WaterMark";
import Loader from "../SkeletonLoader/skeletonLoader";
import { useCanvasImageCloudinaryUpload } from "../../hooks/useCanvasImageCloudinaryUpload";
import CompressionConfirmModal from "../ImageCompressionModal/CompressionConfirmModal";



const CanvasWrapper = ({ contentLoading, setContentLoading }) => {
  const { saveBoardData, loadBoardFile, boardFileName } = useBoardFile();
  const [update, setUpdate] = useState(null)
  const history = useHistory();
  const {
    canvasRef,
    prepareCanvas,
    createGrid,
    Panning,
    createImage,
    getActiveObject,
    saveStateToUndoStack,
    undoStack,
    redoStack,
    reloadStack,
    saveStateToReloadStack,
    savingLastChanges,
    lastChange

  } = useCanvas();

  const {
    setSavingData,
    isReadOnly,
    setIsExternalAllow,
    userInfo,
    documentInfo,
    setIsConflictModalVisible,
    setServerData,
    isConflictModalVisible,
    isLoadingVersion,
    conflictPreviewVerion,
    boardData,
    setBoardData,
    isTemplateDocument,
    templateData,
    setTemplateData,
  } = useDocument();

  let { id } = useParams();

  const {
    addImageToCanvasFromFile,
    compressModalOpen,
    compressFileSize,
    closeCompressModal,
    compressionZIndex,
  } = useCanvasImageCloudinaryUpload({
    roomId: id,
    createImage,
    setSavingData,
    isTemplateDocument,
    messageKey: "wrapper-canvas-image",
  });
  const [movementData, setMovementData] = useState();
  const url_string = window.location.href; //window.location.href
  const url = new URL(url_string);
  const mileboardName = url.searchParams.get("mileboardname");

  const fetchAndLoadTemplate = async (templateId, options = {}) => {
    const { shouldSetTemplateData = false, shouldRemoveFromURL = false } = options;
    const serverResponse = await templateService.fetchTemplateById(templateId);
    const templateData = processTemplateData(serverResponse);

    if (shouldRemoveFromURL) {
      removeTemplateIdFromURL(history);
    }

    if (shouldSetTemplateData) {
      setTemplateData(templateData);
    }

    loadDataIntoCanvas(templateData.mileboardData || serverResponse);

    if (!shouldSetTemplateData) {
      setIsExternalAllow(templateData?.isExternal || false);
      setSavingData({
        isSaving: "",
        lastSavedDateTime: "",
        fileSize: formatFileSize(new TextEncoder().encode(JSON.stringify(templateData.mileboardData)).length),
      });
      // Save the template data to the board database
      await boardServices.updateBoardData(id, templateData.mileboardData, setSavingData, isTemplateDocument);
    }
  };

  const loadDataIntoCanvas = (data) => {
    if (!canvasRef?.current || !data) return;

    canvasRef.current.loadFromJSON(data, () => {
      canvasRef.current.renderAll();
      centerAllObjectsInViewport(canvasRef.current);
      saveStateToReloadStack();
      setContentLoading(false);
    });
  };

  //Send the id to the server.
  //Checks if the board exists, if not. It creates the board.
  //If it does, it loads the while.
  React.useEffect(() => {
    //emitCanvasFile(id);
    !isOffline() && emitRoomID(id);
  }, [id]);

  const baseURL = 'https://board.workw.com/api/';
  // const baseURL = "http://localhost:3000/api/";

  const hasInitializedRef = React.useRef(false);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      prepareCanvas(id);
      hasInitializedRef.current = true;
    }

    const loadData = async () => {
      if (!canvasRef.current) return;
      setContentLoading(true);

      try {

        if (isTemplateDocument) {
          await fetchAndLoadTemplate(id, { shouldSetTemplateData: true });
          return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const templateId = searchParams.get('templateId');
        if (templateId) {
          await fetchAndLoadTemplate(templateId, { shouldRemoveFromURL: true });
          return;
        }

        const offlineBoard = JSON.parse(localStorage.getItem("offlineIdeationData") || "{}");
        const localData = offlineBoard[id]?.data || null;
        const localDate = offlineBoard[id]?.lastUpdateTime ? new Date(offlineBoard[id].lastUpdateTime) : null;

        const serverBoardData = await boardServices.getBoardData(id);
        const serverCanvasData = serverBoardData?.mileboardData?.[0] || null;
        const serverDate = serverBoardData?.updatedAt ? new Date(serverBoardData.updatedAt) : null;
        setServerData(serverBoardData);

        let finalData = null;

        // 1. Preview mode chosen from conflict modal
        if (conflictPreviewVerion) {

          finalData = conflictPreviewVerion === "offline" ? localData : serverCanvasData;
          loadDataIntoCanvas(finalData);
          return;
        }

        // 2. Conflict: both exist, server newer
        if (localData && serverCanvasData && serverDate && localDate && serverDate > localDate) {
          setIsConflictModalVisible(true);
          setContentLoading(false);
          return;
        }

        // 3. Local is newer
        if (localData && (!serverCanvasData || (serverDate && localDate && localDate > serverDate))) {
          finalData = localData;
          loadDataIntoCanvas(localData);
          await boardServices.updateBoardData(id, localData, setSavingData, isTemplateDocument);
          removeOfflineData(id);
          return;
        }

        // 4. Only server available
        if (serverCanvasData) {
          finalData = serverCanvasData;
          loadDataIntoCanvas(serverCanvasData);
          return;
        }

        // 5. No data at all
        setContentLoading(false);

        // Update metadata if we had data
        if (finalData) {
          setIsExternalAllow(finalData?.isExternal || false);
          setSavingData({
            isSaving: "",
            lastSavedDateTime: "",
            fileSize: formatFileSize(new TextEncoder().encode(JSON.stringify(finalData)).length),
          });
        }

      } catch (error) {
        console.error(`Error loading board ${id}:`, error);
        setContentLoading(false);
      }

    };

    loadData();

    return () => {
      // Cleanup if needed
    };
  }, [id, conflictPreviewVerion, isTemplateDocument]);

  React.useEffect(() => {
    //loadBoardFile(id, canvasRef);
    const createBoard = async (id, canvasRef) => {
      try {
        const response = await axios.post(
          `${baseURL}boards/create`,
          { id }
        );

      } catch (error) {
        console.log(error);
      }
    };
    if (!isTemplateDocument) {
      createBoard(id, canvasRef);
    }
  }, [id, canvasRef, isTemplateDocument]);

  React.useEffect(() => {
    const onDragOver = (event) => {
      event.stopPropagation();
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    };

    const onDrop = (event) => {
      event.stopPropagation();
      event.preventDefault();
      const fileList = event.dataTransfer.files;
      for (const file of fileList) {
        if (file.type && !file.type.startsWith("image/")) {
          continue;
        }
        void addImageToCanvasFromFile(file);
      }
    };

    const pasteItem = async () => {
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipboardItem of clipboardItems) {
          for (const type of clipboardItem.types) {
            if (!type || !type.startsWith("image/")) {
              continue;
            }
            const blob = await clipboardItem.getType(type);
            const file = new File([blob], "pasted-image", { type: blob.type || "image/png" });
            await addImageToCanvasFromFile(file);
          }
        }
      } catch (err) {
        console.error(err.name, err.message);
      }
    };

    window.addEventListener("dragover", onDragOver);
    if (isReadOnly) {
      window.addEventListener("drop", onDrop);
    }
    window.addEventListener("paste", pasteItem);

    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", pasteItem);
    };
  }, [id, isReadOnly, addImageToCanvasFromFile]);

  React.useEffect(() => {
    Panning(id);
  }, [Panning]);

  //React.useEffect(() => {
  //  createGrid();
  //}, [createGrid]);



  //resize canvas according to screensize.
  // React.useEffect(() => {
  //   const resizeCanvas = () => {
  //     canvasRef.current.setDimensions({
  //       width: width,
  //       height: height,
  //     });
  //     canvasRef.current.renderAll();
  //   };
  //   window.addEventListener("resize", resizeCanvas);
  //   return () => window.removeEventListener("resize", resizeCanvas);
  // }, [canvasRef, width, height]);

  //event listeners for canvas
  React.useEffect(() => {

    if (isLoadingVersion.current) return;

    if (canvasRef && !isConflictModalVisible && !conflictPreviewVerion) {
      //Executed when an object is modified, i.e the dimensions,color etc.
      canvasRef.current.on("object:modified", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being moved
            textBox.exitEditing();
          }
          const modifiedObj = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(canvasRef.current.toJSON(modifiedObj));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          !isOffline() && emitModify(id, modifiedObj);
          //saveBoardData(id, canvasRef);
        }
      });
      //Executed when text is changed.
      canvasRef.current.on("text:changed", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          const modifiedTxt = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(modifiedTxt);
          // console.log(canvasRef.current.toJSON(modifiedTxt));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          !isOffline() && emitModify(id, modifiedTxt);
          saveBoardData(id, canvasRef);
        }
      });
      //Executed when the object is moving.
      canvasRef.current.on("object:moving", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being moved
            textBox.exitEditing();
          }
          const modifiedObj = {
            obj: options.target,
            id: options.target.id,
          };
          // console.log(canvasRef.current.toJSON(modifiedObj));
          setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
          setMovementData(modifiedObj);
          // emitModify(modifiedObj);
          saveBoardData(id, canvasRef);
        }
      });
      canvasRef.current.on("path:created", function (e) {

        if (isLoadingVersion.current) return;

        let path = e.path;
        path.set({ id: uuid() });
        path.set({ type: "path" });
        // console.log(canvasRef.current.toJSON({path}));
        setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
        saveStateToUndoStack()                  //Path created with pen tool saves in UndoStack here                     //Implemented to make Undo Redo work for Pen tool
        !isOffline() && emitDrawingPath({ room: id, path, id: path.id });
        saveBoardData(id, canvasRef);
      });
      canvasRef.current.on("object:removed", function (options) {

        if (isLoadingVersion.current) return;

        if (options.target) {
          if (options.target.id && options.target.id.includes('sticky-note')) {
            const stickyNote = options.target;
            const textBox = stickyNote.item(1);
            // Exit editing mode when the sticky note is being removed
            textBox.exitEditing();
          }
        }
        setBoardData(canvasRef.current.toJSON(["selectable", "id", "type"]));
      });

      // canvasRef, saveBoardData, boardFileName, getActiveObject, id, isConflictModalVisible, isLoadingVersion

      modifyObj(canvasRef, saveStateToUndoStack, setBoardData);
      addObj(canvasRef, saveStateToUndoStack, setBoardData);
      addText(canvasRef, saveStateToUndoStack, setBoardData);
      addPath(canvasRef, saveStateToUndoStack, setBoardData);
      removeObject(canvasRef, saveStateToUndoStack, setBoardData);
      addImage(canvasRef, saveStateToUndoStack, setBoardData);
      addClonedObject(canvasRef, saveStateToUndoStack, setBoardData);
      addToGroup(canvasRef, saveStateToUndoStack, setBoardData);
      //Below Function is Socket Listener Function which listens to background changes and reflects in connected clients,setBoardData C
      onBackgroundColorChange(canvasRef, saveStateToUndoStack, setBoardData)
      //Function below Listen to Server when clear-canvas is emitt,setBoardDataed
      clearCanvasListen(canvasRef, saveStateToUndoStack, setBoardData);
      onShapeColorChange(canvasRef, saveStateToUndoStack, setBoardData);
      //Listener to listen to Undo Emit from Serv,setBoardDataer
      ListenUndo(canvasRef, undoStack, redoStack, saveStateToUndoStack, setBoardData);
      //Listener to listen to Redo Emit from Serv,setBoardDataer
      ListenRedo(canvasRef, undoStack, redoStack, saveStateToUndoStack, setBoardData);
      !isOffline() && listenSendToBack(canvasRef, saveStateToUndoStack, setBoardData);
      isOffline() && listenBringToFront(canvasRef, saveStateToUndoStack, setBoardData);
      onStrokeColorChange(canvasRef, saveStateToUndoStack, setBoardData)
      !isOffline() && listenUngroup(canvasRef, saveStateToUndoStack, setBoardData);
      listenUpdateAll(id, canvasRef, saveStateToUndoStack, setBoardData);
    }
  }, [canvasRef, saveBoardData, boardFileName, getActiveObject, id, isConflictModalVisible, isLoadingVersion]);

  React.useEffect(() => {

    if (boardData && boardData.length !== 0) {
      const updateDataOnServer = async (data) => {
        const isOffline = !window.navigator.onLine;
        if (isOffline) {
          // Save to localStorage if offline
          saveToLocalStorage(id, data);
          return;
        }
        await boardServices.updateBoardData(id, data, setSavingData, isTemplateDocument);
        // Clear localStorage after successful sync
        removeOfflineData(id);
        workwiseServices.saveDocumentlastUpdate(id, setSavingData, data);
      };

      let timer = setTimeout(async () => {
        updateDataOnServer(boardData);
      }, 100);

      return () => clearTimeout(timer);

    }
  }, [boardData, undoStack, redoStack, isTemplateDocument]);

  React.useEffect(() => {
    let timer = setTimeout(() => {
      !isOffline() && emitModify(movementData);
      //  console.log("timer function called");
    }, 500)

    return () => clearTimeout(timer);

  }, [movementData])


  return (
    <>
      {contentLoading && <Loader />}
      <div style={{
        pointerEvents: isReadOnly ? 'none' : 'auto',
        visibility: contentLoading ? 'hidden' : 'visible'
      }} id='my-canvas-board'>
        {isReadOnly && userInfo?.userId !== documentInfo?.creator?.id && !conflictPreviewVerion && <WaterMark />}
        <Canvas />
      </div>
      <CompressionConfirmModal
        open={compressModalOpen}
        fileSize={compressFileSize}
        onCompress={() => closeCompressModal('compress')}
        onUploadOriginal={() => closeCompressModal('original')}
        onCancel={() => closeCompressModal('cancel')}
        zIndex={compressionZIndex}
        getContainer={() => document.body}
      />
    </>
  );
};

export default CanvasWrapper;
